import { useState } from "react";
import { Menu, X, User, LogOut, Shield, LayoutDashboard, History } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import blueluxLogo from "@/assets/bluelux-logo.png";

export const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, isAdmin, userProfile } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = () => {
    // Navigate to logout page which handles the full cross-domain logout flow
    navigate('/auth/logout');
  };

  const getInitials = () => {
    if (userProfile?.first_name && userProfile?.last_name) {
      const firstInitial = userProfile.first_name.charAt(0).toUpperCase();
      const lastInitial = userProfile.last_name.charAt(0).toUpperCase();
      return `${firstInitial}${lastInitial}`;
    }
    if (!user?.email) return "U";
    return user.email.substring(0, 2).toUpperCase();
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <a href="https://bluelux.com" className="flex items-center">
                <img 
                  src={blueluxLogo} 
                  alt="BlueLux Pool Service" 
                  className="h-8 md:h-10 w-auto"
                />
              </a>
            </div>

            <div className="hidden lg:flex items-center flex-1 justify-center">
              <nav className="flex items-center space-x-6">
                <Link to="/" className="text-foreground hover:text-primary transition-colors">
                  Home
                </Link>
                <Link to="/wiki/onboarding/welcome" className="text-foreground hover:text-primary transition-colors">
                  Onboarding
                </Link>
                <Link to="/wiki/service/standards" className="text-foreground hover:text-primary transition-colors">
                  Service
                </Link>
                <Link to="/wiki/compensation/pay-incentives" className="text-foreground hover:text-primary transition-colors">
                  Pay
                </Link>
                <Link to="/wiki/policies/ic-status" className="text-foreground hover:text-primary transition-colors">
                  Policies
                </Link>
                <Link to="/wiki/resources/route-coverage" className="text-foreground hover:text-primary transition-colors">
                  Resources
                </Link>
              </nav>
            </div>
              
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger className="hidden lg:flex items-center gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback>{getInitials()}</AvatarFallback>
                  </Avatar>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <div className="px-2 py-1.5 text-sm">
                    <p className="font-medium">{user.email}</p>
                    {isAdmin && <p className="text-xs text-muted-foreground">Admin</p>}
                  </div>
                  <DropdownMenuSeparator />
                  {isAdmin && (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/admin/users" className="flex items-center gap-2 cursor-pointer">
                          <Shield className="h-4 w-4" />
                          Admin Panel
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/admin/activity-logs" className="flex items-center gap-2 cursor-pointer">
                          <History className="h-4 w-4" />
                          Activity Logs
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem asChild>
                    <Link to="/profile/dashboard" className="flex items-center gap-2 cursor-pointer">
                      <LayoutDashboard className="h-4 w-4" />
                      My Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/profile/settings" className="flex items-center gap-2 cursor-pointer">
                      <User className="h-4 w-4" />
                      Profile Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleSignOut} className="flex items-center gap-2 cursor-pointer text-destructive">
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2"
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {isMenuOpen && (
            <div className="lg:hidden mt-4 py-4 border-t">
              <nav className="flex flex-col space-y-4">
                <Link
                  to="/"
                  className="text-foreground hover:text-primary transition-colors py-2 px-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Home
                </Link>
                <Link
                  to="/wiki/onboarding/welcome"
                  className="text-foreground hover:text-primary transition-colors py-2 px-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Onboarding
                </Link>
                <Link
                  to="/wiki/service/standards"
                  className="text-foreground hover:text-primary transition-colors py-2 px-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Service
                </Link>
                <Link
                  to="/wiki/compensation/pay-incentives"
                  className="text-foreground hover:text-primary transition-colors py-2 px-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Pay
                </Link>
                <Link
                  to="/wiki/policies/ic-status"
                  className="text-foreground hover:text-primary transition-colors py-2 px-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Policies
                </Link>
                <Link
                  to="/wiki/resources/route-coverage"
                  className="text-foreground hover:text-primary transition-colors py-2 px-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Resources
                </Link>
              </nav>
            </div>
          )}
        </div>
      </header>
      
      {isMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
          onClick={() => setIsMenuOpen(false)}
          aria-hidden="true"
        />
      )}
    </>
  );
};
